import React, { useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { useAuthStore } from "../../store/authStore";
import { useLoaderStore } from "../../store/loaderStore";
import { canMobilePunch, MOBILE_PUNCH_DENIED_MESSAGE } from "../../utils/mobilePunchAccess";
import "./BottomNav.css";
 
import { FiUser, FiCheckSquare, FiFileText } from "react-icons/fi";
import { AiFillHome } from "react-icons/ai";
import faceScanIcon from "../../assets/face-scan.png";
 
const BottomBar = ({ onPunchClick, punchLabel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { showLoader, hideLoader } = useLoaderStore();
  const checkingShift = useRef(false);
  const mobilePunchAllowed = canMobilePunch(user);
 
 
  const isAdmin = location.pathname.startsWith("/admin/mobile");
  const basePath = isAdmin ? "/admin/mobile" : "/user/mobile";
 
  const leftItems = [
    {
      path: `${basePath}/dashboard`,
      label: "Home",
      icon: <AiFillHome />,
    },
    {
      path: `${basePath}/attendance`,
      label: "Attendance",
      icon: <FiUser />,
      // sub-pages opened from the attendance screen
      subPaths: [
        `${basePath}/attendance-log`,
        `${basePath}/team-attendance`,
        `${basePath}/attendanceform`,
      ],
    },
  ];
 
  const rightItems = [
    {
      path: `${basePath}/approvals`,
      label: "Approvals",
      icon: <FiCheckSquare />,
    },
    {
      path: `${basePath}/my-requests`,
      label: "Requests",
      icon: <FiFileText />,
    },
  ];
 
  /*
   * The dashboard passes its own handler (today's shift is already loaded
   * there). Every other page checks today's shift here and opens the punch
   * screen straight away; Punchmark then returns to this page.
   */
  const handlePunch = async () => {
    if (!mobilePunchAllowed) {
      toast.info(MOBILE_PUNCH_DENIED_MESSAGE);
      return;
    }
    if (onPunchClick) {
      onPunchClick();
      return;
    }

    if (checkingShift.current || !user?.company_id || !user?.user_id) return;
    checkingShift.current = true;

    try {
      showLoader();

      const response = await api.get(
        `/attendance/shift-for-date?company_id=${user.company_id}&user_id=${user.user_id}&target_date=${moment().format("YYYY-MM-DD")}`,
      );
      const result = await response.json();
      const shift = result.status ? result : null;

      // same checks as the dashboard of each role
      if (
        !shift ||
        !shift.shift_name ||
        !shift.shift_start_time ||
        !shift.shift_end_time
      ) {
        toast.warning("Shift is not assigned for today.");
        return;
      }

      if (isAdmin && shift.type === "Weekoff") {
        toast.info(shift.message || "Punching is not allowed on a Week Off.");
        return;
      }

      if (isAdmin && shift.type === "Holiday") {
        toast.info(shift.message || "Punching is not allowed on a Holiday.");
        return;
      }

      navigate(`${basePath}/punchmark`, {
        state: { from: location.pathname },
      });
    } catch (err) {
      console.error("Shift API error:", err);
      toast.error("Unable to check today's shift. Please try again.");
    } finally {
      hideLoader();
      checkingShift.current = false;
    }
  };
 
  const renderItem = (item) => (
    <button
      key={item.path}
      onClick={() => navigate(item.path)}
      className={`nav-item ${
        location.pathname === item.path ||
        item.subPaths?.includes(location.pathname)
          ? "active"
          : ""
      }`}
    >
      <span className="nav-icon react-icon">{item.icon}</span>
      <span className="nav-label">{item.label}</span>
    </button>
  );
 
  return (
    <div className="bottom-nav">
      {leftItems.map(renderItem)}
 
      <div className="nav-punch-slot">
        <button
          type="button"
          className="nav-punch-btn"
          onClick={handlePunch}
          disabled={!mobilePunchAllowed}
          aria-label={mobilePunchAllowed ? punchLabel || "Punch" : MOBILE_PUNCH_DENIED_MESSAGE}
          title={mobilePunchAllowed ? punchLabel || "Punch" : MOBILE_PUNCH_DENIED_MESSAGE}
        >
          <img src={faceScanIcon} alt="" className="nav-punch-icon" />
          <span className="nav-punch-scan" aria-hidden="true" />
        </button>
      </div>
 
      {rightItems.map(renderItem)}
    </div>
  );
};
 
export default BottomBar;
